// Group-aware reordering for the question overview list.
//
// Conditional questions render as sub-questions (8.1, 8.2) under the question
// they depend on. Moving them with plain index swaps would let a sub-question
// land above its own parent, which breaks the numbering in computeDisplayLabels.
// These helpers treat a question and its sub-questions as one block instead.

// Groups questions into [{ root, children }] blocks.
//
// A question attaches to the group of the question it depends on. Anything whose
// parent is missing — or appears later in the list, which malformed data can do —
// becomes its own root so the list still renders.
export const buildQuestionGroups = (questions = []) => {
  const list = Array.isArray(questions) ? questions : [];
  const groups = [];
  const groupIndexById = new Map();

  list.forEach((question) => {
    const parentId = question?.conditional?.dependsOn;
    const parentGroupIndex = parentId == null ? undefined : groupIndexById.get(String(parentId));

    if (parentGroupIndex === undefined) {
      groupIndexById.set(String(question?.id), groups.length);
      groups.push({ root: question, children: [] });
      return;
    }

    // Deeper nesting collapses into the root block, alongside its own parent.
    groupIndexById.set(String(question?.id), parentGroupIndex);
    groups[parentGroupIndex].children.push(question);
  });

  return groups;
};

const flattenGroups = (groups) => groups.flatMap((group) => [group.root, ...group.children]);

const findPosition = (groups, targetId) => {
  for (let groupIndex = 0; groupIndex < groups.length; groupIndex += 1) {
    if (String(groups[groupIndex].root?.id) === targetId) {
      return { groupIndex, childIndex: -1 };
    }
    const childIndex = groups[groupIndex].children.findIndex((child) => String(child?.id) === targetId);
    if (childIndex !== -1) {
      return { groupIndex, childIndex };
    }
  }
  return null;
};

// Per-row display and control state, keyed by question id. A root can move as
// far as the group list allows; a sub-question only swaps with its siblings.
// `isSubQuestion` drives the indent, and follows the same grouping rules — an
// orphan child reads as a root here too, so it is never indented under nothing.
export const getQuestionRowMeta = (questions = []) => {
  const groups = buildQuestionGroups(questions);
  const rowMeta = new Map();

  groups.forEach((group, groupIndex) => {
    rowMeta.set(String(group.root?.id), {
      canMoveUp: groupIndex > 0,
      canMoveDown: groupIndex < groups.length - 1,
      isSubQuestion: false
    });
    group.children.forEach((child, childIndex) => {
      rowMeta.set(String(child?.id), {
        canMoveUp: childIndex > 0,
        canMoveDown: childIndex < group.children.length - 1,
        isSubQuestion: true
      });
    });
  });

  return rowMeta;
};

// Drops the question at `fromIndex` onto the row at `toIndex` and returns a new
// array. A dragged question carries its sub-questions along and lands where the
// target's block sits; a dragged sub-question stays inside its own block.
// Returns the original array unchanged when the drop is not possible.
export const reorderQuestionsByDrag = (questions = [], fromIndex, toIndex) => {
  const list = Array.isArray(questions) ? questions : [];
  const fromQuestion = list[fromIndex];
  const toQuestion = list[toIndex];
  if (!fromQuestion || !toQuestion || fromIndex === toIndex) return questions;

  const groups = buildQuestionGroups(list);
  const from = findPosition(groups, String(fromQuestion.id));
  const to = findPosition(groups, String(toQuestion.id));
  if (!from || !to) return questions;

  if (from.childIndex === -1) {
    if (from.groupIndex === to.groupIndex) return questions;

    const reorderedGroups = [...groups];
    const [movedGroup] = reorderedGroups.splice(from.groupIndex, 1);
    reorderedGroups.splice(to.groupIndex, 0, movedGroup);
    return flattenGroups(reorderedGroups);
  }

  // Dropping a sub-question outside its own block would break its numbering.
  if (to.groupIndex !== from.groupIndex || to.childIndex === -1) return questions;

  const reorderedChildren = [...groups[from.groupIndex].children];
  const [movedChild] = reorderedChildren.splice(from.childIndex, 1);
  reorderedChildren.splice(to.childIndex, 0, movedChild);

  const reorderedGroups = groups.map((group, i) => (
    i === from.groupIndex ? { ...group, children: reorderedChildren } : group
  ));
  return flattenGroups(reorderedGroups);
};

// Moves the question at `index` one slot up or down and returns a new array.
// Returns the original array unchanged when the move is not possible.
export const moveQuestionInList = (questions = [], index, direction) => {
  const list = Array.isArray(questions) ? questions : [];
  const question = list[index];
  if (!question) return questions;

  if (direction !== 'up' && direction !== 'down') return questions;
  const delta = direction === 'up' ? -1 : 1;

  const groups = buildQuestionGroups(list);
  const position = findPosition(groups, String(question.id));
  if (!position) return questions;

  const { groupIndex, childIndex } = position;

  if (childIndex === -1) {
    const targetGroupIndex = groupIndex + delta;
    if (targetGroupIndex < 0 || targetGroupIndex >= groups.length) return questions;

    const reorderedGroups = [...groups];
    [reorderedGroups[groupIndex], reorderedGroups[targetGroupIndex]] =
      [reorderedGroups[targetGroupIndex], reorderedGroups[groupIndex]];
    return flattenGroups(reorderedGroups);
  }

  const siblings = groups[groupIndex].children;
  const targetChildIndex = childIndex + delta;
  if (targetChildIndex < 0 || targetChildIndex >= siblings.length) return questions;

  const reorderedChildren = [...siblings];
  [reorderedChildren[childIndex], reorderedChildren[targetChildIndex]] =
    [reorderedChildren[targetChildIndex], reorderedChildren[childIndex]];

  const reorderedGroups = groups.map((group, i) => (
    i === groupIndex ? { ...group, children: reorderedChildren } : group
  ));
  return flattenGroups(reorderedGroups);
};
